import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import usePageTitle from 'hooks/usePageTitle';
import useEscapeClose from 'hooks/useEscapeClose';

const EMPTY = { titre: '', date_reunion: '', lieu: '', participants: '', ordre_du_jour: '', contenu: '', decisions: '', actions_a_suivre: '' };

export default function ComptesRendus() {
  usePageTitle('Comptes rendus');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ statut: '', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [listRes, statsRes] = await Promise.all([
        api.get('/assistanat/comptes-rendus', { params }),
        api.get('/assistanat/comptes-rendus/stats'),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre) return alert('Titre requis');
    setSaving(true);
    try {
      const payload = {
        ...form,
        participants: form.participants ? form.participants.split(',').map(p => p.trim()).filter(Boolean) : [],
      };
      if (editId) {
        await api.put(`/assistanat/comptes-rendus/${editId}`, payload);
      } else {
        await api.post('/assistanat/comptes-rendus', payload);
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({
      titre: item.titre || '', date_reunion: item.date_reunion?.split('T')[0] || '',
      lieu: item.lieu || '', participants: (item.participants || []).join(', '),
      ordre_du_jour: item.ordre_du_jour || '', contenu: item.contenu || '',
      decisions: item.decisions || '', actions_a_suivre: item.actions_a_suivre || '',
      statut: item.statut,
    });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce compte rendu ?')) return;
    try {
      await api.delete(`/assistanat/comptes-rendus/${id}`);
      load();
    } catch {}
  };

  const valider = async (id) => {
    try {
      await api.put(`/assistanat/comptes-rendus/${id}`, { statut: 'valide' });
      load();
    } catch {}
  };

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const setFld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
<div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg></div></div>
          <div className="stat-card-value">{stats.total || 0}</div>
          <div className="stat-card-sub">comptes rendus</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Brouillons</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{stats.brouillon || 0}</div>
          <div className="stat-card-sub">en attente de validation</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Valides</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{stats.valide || 0}</div>
          <div className="stat-card-sub">comptes rendus finalises</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="form-input" placeholder="Rechercher..." value={filters.search}
              onChange={e => setF('search', e.target.value)} style={{ maxWidth: 200 }} />
            <select className="form-input" value={filters.statut} onChange={e => setF('statut', e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">Tous statuts</option>
              <option value="brouillon">Brouillon</option>
              <option value="valide">Valide</option>
            </select>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }}>
            + Nouveau compte rendu
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Date reunion</th>
                  <th>Lieu</th>
                  <th>Participants</th>
                  <th>Statut</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Aucun compte rendu</td></tr>
                )}
                {items.map(item => (
                  <tr key={item.id}>
                    <td><strong>{item.titre}</strong></td>
                    <td>{item.date_reunion ? new Date(item.date_reunion).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{item.lieu || '-'}</td>
                    <td>
                      {item.participants && item.participants.length > 0
                        ? item.participants.slice(0, 3).join(', ') + (item.participants.length > 3 ? ` +${item.participants.length - 3}` : '')
                        : '-'}
                    </td>
                    <td>
                      <span className={`gss-badge ${item.statut === 'valide' ? 'gss-badge-green' : ''}`}
                        style={item.statut === 'brouillon' ? { background: '#FFF3E0', color: '#F57C00' } : {}}>
                        {item.statut === 'brouillon' ? 'Brouillon' : 'Valide'}
                      </span>
                    </td>
                    <td>
                      <div className="gss-actions">
                        <button className="gss-btn-sm" onClick={() => setDetail(item)}>Voir</button>
                        <button className="gss-btn-icon" onClick={() => handleEdit(item)} title="Modifier">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {item.statut === 'brouillon' && (
                          <button className="gss-btn-sm" style={{ background: '#E8F5E9', color: '#2E7D32' }} onClick={() => valider(item.id)}>Valider</button>
                        )}
                        <button className="gss-btn-icon gss-btn-icon--delete" onClick={() => handleDelete(item.id)} title="Archiver">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DETAIL */}
      {detail && (
        <div className="gss-modal-overlay" onClick={() => setDetail(null)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="gss-modal-header">
              <h2>{detail.titre}</h2>
              <button className="gss-modal-close" onClick={() => setDetail(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                <div><span className="form-label">Date reunion :</span> {detail.date_reunion ? new Date(detail.date_reunion).toLocaleDateString('fr-FR') : '-'}</div>
                <div><span className="form-label">Lieu :</span> {detail.lieu || '-'}</div>
                <div style={{ gridColumn: '1/-1' }}><span className="form-label">Participants :</span> {(detail.participants || []).join(', ') || '-'}</div>
              </div>
              {detail.ordre_du_jour && (
                <div style={{ marginBottom: '1rem' }}>
                  <div className="form-label">Ordre du jour</div>
                  <div style={{ padding: '0.75rem', background: '#F9FAFB', borderRadius: 6, whiteSpace: 'pre-wrap' }}>{detail.ordre_du_jour}</div>
                </div>
              )}
              {detail.contenu && (
                <div style={{ marginBottom: '1rem' }}>
                  <div className="form-label">Contenu</div>
                  <div style={{ padding: '0.75rem', background: '#F9FAFB', borderRadius: 6, whiteSpace: 'pre-wrap' }}>{detail.contenu}</div>
                </div>
              )}
              {detail.decisions && (
                <div style={{ marginBottom: '1rem' }}>
                  <div className="form-label">Decisions</div>
                  <div style={{ padding: '0.75rem', background: '#E8F5E9', borderRadius: 6, whiteSpace: 'pre-wrap' }}>{detail.decisions}</div>
                </div>
              )}
              {detail.actions_a_suivre && (
                <div style={{ marginBottom: '1rem' }}>
                  <div className="form-label">Actions a suivre</div>
                  <div style={{ padding: '0.75rem', background: '#FFF3E0', borderRadius: 6, whiteSpace: 'pre-wrap' }}>{detail.actions_a_suivre}</div>
                </div>
              )}
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn" onClick={() => setDetail(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* FORMULAIRE MODAL */}
      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                <h2>{editId ? 'Modifier le compte rendu' : 'Nouveau compte rendu'}</h2>
                <div className="gss-modal-subtitle">Redaction de compte rendu</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                  <span>INFORMATIONS GENERALES</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Titre *</label>
                    <input className="form-input" value={form.titre} onChange={e => setFld('titre', e.target.value)} required />
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Date de reunion</label>
                    <input className="form-input" type="date" value={form.date_reunion} onChange={e => setFld('date_reunion', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label className="form-label">Lieu</label>
                    <input className="form-input" value={form.lieu} onChange={e => setFld('lieu', e.target.value)} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Participants (separes par des virgules)</label>
                    <input className="form-input" value={form.participants} onChange={e => setFld('participants', e.target.value)}
                      placeholder="Ex: M. Dupont, Mme Martin, Dr. Diallo" />
                  </div>
                  {editId && (
                    <div className="gss-field">
                      <label className="form-label">Statut</label>
                      <select className="form-input" value={form.statut} onChange={e => setFld('statut', e.target.value)}>
                        <option value="brouillon">Brouillon</option>
                        <option value="valide">Valide</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/>
                  </svg>
                  <span>CONTENU DE LA REUNION</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Ordre du jour</label>
                    <textarea className="form-input" rows="3" value={form.ordre_du_jour} onChange={e => setFld('ordre_du_jour', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Contenu / Notes de reunion</label>
                    <textarea className="form-input" rows="5" value={form.contenu} onChange={e => setFld('contenu', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                  </svg>
                  <span>DECISIONS ET SUIVI</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Decisions prises</label>
                    <textarea className="form-input" rows="3" value={form.decisions} onChange={e => setFld('decisions', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label className="form-label">Actions a suivre</label>
                    <textarea className="form-input" rows="3" value={form.actions_a_suivre} onChange={e => setFld('actions_a_suivre', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                </div>

              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg> Enregistrer</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
